import AuditInfo from 'components/audit/AuditInfo';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { filterRowList, Row } from 'components/GridItemList';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import MoneyDisplay from 'components/util/MoneyDisplay';
import React, { useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ClassifierData } from 'types/classifiers';
import { ExpenseDetails } from 'types/expenses';
import { getLabelWithDescription } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { ExpenseStatusLabel } from '../ExpenseStatusLabel';
import { ExpenseSection } from '../form-sections/ExpenseSection';

const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

interface Props {
    expense: ExpenseDetails;
    classifiers: Record<string, ClassifierData>;
    /** The new design: the Expense and Booking sections, each edited in place. */
    isNew?: boolean;
}

const GeneralView: React.FC<Props> = ({ expense, classifiers, isNew = false }) => {
    // On a draft, the values it was saved without read as missing, and whom it is shared with shows.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    const sharedWith = draftActions ? <DraftSharedWith /> : null;
    const rows = useMemo(
        () => ({
            type: {
                label: t('expenses.type'),
                formLabels: [t('expenses.form.expense-type')],
                content: expense.expenseTypeCode
                    ? getClassifierText(classifiers['EXPENSE_TYPE'], expense.expenseTypeCode) || '-'
                    : '-',
            },
            amount: {
                label: t('expenses.amount'),
                content:
                    expense.expenseAmount != null ? (
                        <MoneyDisplay amount={expense.expenseAmount} currency={expense.currencyTypeCode ?? ''} />
                    ) : (
                        '-'
                    ),
            },
            originalAmount: {
                label: t('expenses.amount-original-currency'),
                formLabels: [t('expenses.form.amount'), t('expenses.form.currency')],
                content:
                    expense.expenseAmountOriginalCurrency != null && expense.originalCurrencyTypeCode ? (
                        <MoneyDisplay
                            amount={expense.expenseAmountOriginalCurrency}
                            currency={expense.originalCurrencyTypeCode}
                        />
                    ) : (
                        '-'
                    ),
            },
            date: {
                label: t('date'),
                content: <FormattedDate value={expense.expenseDate} />,
            },
            matter: {
                label: t('expenses.form.case-client'),
                content: expense.matter ? (
                    <span>
                        <RouterLink
                            className={LINK_CLASS}
                            to={`/matter/${expense.matter.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {expense.matter.fullName}
                        </RouterLink>
                        {expense.matter.clientParty ? (
                            <>
                                {' / '}
                                <RouterLink
                                    className={LINK_CLASS}
                                    to={`/contact/${expense.matter.clientParty.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {expense.matter.clientParty.name}
                                </RouterLink>
                            </>
                        ) : null}
                    </span>
                ) : (
                    '-'
                ),
            },
            effort:
                expense.effortId != null
                    ? {
                          label: t('expenses.bd-effort'),
                          content: (
                              <RouterLink className={LINK_CLASS} to={`/effort/${expense.effortId}`}>
                                  {t('expenses.bd-effort')}
                              </RouterLink>
                          ),
                      }
                    : null,
            timecard:
                expense.timecardId != null
                    ? {
                          label: t('expenses.timecard'),
                          content: (
                              <RecordLink
                                  className={LINK_CLASS}
                                  to={`/timecards/${expense.timecardId}`}
                                  label={`#${expense.timecardId}`}
                              />
                          ),
                      }
                    : null,
            employee: {
                label: t('expenses.added-by'),
                content: expense.employee ? (
                    <EmployeeNameLink
                        className={LINK_CLASS}
                        to={`/employee/${expense.employee.id}`}
                        entity={expense.employee}
                    />
                ) : (
                    '-'
                ),
            },
            status: {
                label: t('expenses.status'),
                // ExpenseStatusLabel intentionally renders nothing for non-billable expenses, so
                // hide the whole row in that case (the billable state has its own row).
                hidden: expense.isNonBillable,
                content: expense.expenseStatusTypeCode ? (
                    <ExpenseStatusLabel
                        classifier={classifiers['EXPENSE_STATUS_TYPE']}
                        statusCode={expense.expenseStatusTypeCode}
                        isNonBillable={expense.isNonBillable}
                    />
                ) : (
                    '-'
                ),
            },
            nonBillable: {
                label: t('expenses.form.is-non-billable'),
                content: expense.isNonBillable ? t('yes') : t('no'),
            },
            externalId: expense.externalId
                ? {
                      label: t('expenses.external-id'),
                      content: expense.externalId,
                  }
                : null,
            description: {
                label: t('description'),
                content: expense.description || '-',
                multiline: true,
            },
            invoices:
                expense.invoices.length > 0
                    ? {
                          label: t('expenses.connected-invoices'),
                          content: (
                              <div className="flex flex-col">
                                  {expense.invoices.map((invoice) => (
                                      <RecordLink
                                          className={LINK_CLASS}
                                          key={invoice.id}
                                          to={`/invoice/${invoice.id}`}
                                          label={invoice.invoiceNumber}
                                      />
                                  ))}
                              </div>
                          ),
                      }
                    : null,
        }),
        [expense, classifiers]
    );

    const auditInfo = (
        <AuditInfo
            createdAt={expense.createdAt}
            createdByEmployee={expense.createdByEmployee}
            lastModifiedAt={expense.lastModifiedAt ?? undefined}
            changedByEmployee={expense.changedByEmployee}
        />
    );

    if (isNew) {
        const { matter } = expense;
        // A section lists the expense form's fields for it in the form's order, each under the field's label and
        // drawn as the field shows it: the type with its description, the amount and its currency apart.
        const expenseRows: Row[] = [
            {
                label: t('expenses.form.expense-type'),
                content: expense.expenseTypeCode
                    ? getLabelWithDescription(classifiers['EXPENSE_TYPE'])({
                          label: getClassifierText(classifiers['EXPENSE_TYPE'], expense.expenseTypeCode),
                          value: expense.expenseTypeCode,
                      }) || '-'
                    : '-',
            },
            {
                label: t('expenses.form.amount'),
                content:
                    expense.expenseAmountOriginalCurrency != null ? (
                        <MoneyDisplay amount={expense.expenseAmountOriginalCurrency} />
                    ) : (
                        '-'
                    ),
            },
            {
                label: t('expenses.form.currency'),
                content: expense.originalCurrencyTypeCode
                    ? getClassifierText(classifiers['CURRENCY'], expense.originalCurrencyTypeCode) || '-'
                    : '-',
            },
            rows.date,
            { ...rows.description, label: t('expenses.form.details-of-expense') },
        ];
        // What the expense is booked on, then what the system derives from it and no field edits: the amount in
        // the base currency, the billing status, and the records it came from or went to.
        const bookingRows: Row[] = filterRowList([
            {
                ...rows.matter,
                // The case on the select's line and its client on the line the form has under the select.
                content: matter ? (
                    <div className="-mb-2 flex min-w-0 flex-col">
                        <EllipsisTooltip>
                            <RecordLink
                                className={LINK_CLASS}
                                to={`/matter/${matter.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                label={matter.fullName}
                                hideTitle
                            />
                        </EllipsisTooltip>
                        {matter.clientParty ? (
                            <span className="mt-3 truncate font-normal">
                                {t('expenses.form.client')}:{' '}
                                <RecordLink
                                    className={LINK_CLASS}
                                    to={`/contact/${matter.clientParty.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    label={matter.clientParty.name}
                                />
                            </span>
                        ) : null}
                    </div>
                ) : (
                    '-'
                ),
            },
            { ...rows.employee, label: t('expenses.form.added-by') },
            rows.nonBillable,
            { ...rows.amount, readOnly: true },
            { ...rows.status, readOnly: true },
            rows.effort && { ...rows.effort, readOnly: true },
            rows.timecard && { ...rows.timecard, readOnly: true },
            rows.externalId && { ...rows.externalId, readOnly: true },
            rows.invoices && { ...rows.invoices, readOnly: true },
        ]);
        const booking = (
            <ExpenseSection id="booking" titleId="expenses.booking" expense={expense} rows={bookingRows}>
                {(edit) => (
                    <GridItemList
                        isNew
                        titleId="expenses.booking"
                        data={withMissing(bookingRows, missingFields)}
                        button={edit}
                    />
                )}
            </ExpenseSection>
        );
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    <ExpenseSection id="expense" titleId="expenses.expense" expense={expense}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="expenses.expense"
                                data={withMissing(expenseRows, missingFields)}
                                button={edit}
                            />
                        )}
                    </ExpenseSection>
                    {auditInfo}
                </div>
                {sharedWith ? (
                    <div className="flex min-w-0 flex-col gap-(--card-gap)">
                        {sharedWith}
                        {booking}
                    </div>
                ) : (
                    booking
                )}
            </div>
        );
    }

    const overviewRows: Row[] = filterRowList([
        rows.type,
        rows.amount,
        rows.originalAmount,
        rows.date,
        rows.matter,
        rows.effort,
        rows.timecard,
        rows.employee,
        rows.status,
        rows.nonBillable,
        rows.externalId,
        rows.description,
        rows.invoices,
    ]);

    return (
        <div className="flex flex-col gap-6">
            <GridItemList
                titleId="matters.overview"
                size={12}
                data={withMissing(overviewRows, missingFields)}
                key="expense-overview"
            />
            {sharedWith}
            {auditInfo}
        </div>
    );
};

export default GeneralView;
